import { useEffect, useState } from 'react';
import './MentionAutocomplete.css';

interface User {
  id: string;
  username: string;
  display_name: string;
  odyssey_title: string;
  odyssey_level: number;
  is_online: boolean;
}

interface MentionAutocompleteProps {
  query: string;
  onSelect: (user: User) => void;
  position: { top: number; left: number };
}

export function MentionAutocomplete({ query, onSelect, position }: MentionAutocompleteProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Allow search with 1+ characters for friends (less strict than non-friends)
    if (query.length < 1) {
      setUsers([]);
      return;
    }

    const searchUsers = async () => {
      setLoading(true);
      try {
        // Get token from localStorage (set by authStore)
        const authState = localStorage.getItem('auth-storage');
        const token = authState ? JSON.parse(authState).state.token : null;

        // Search only friends for @mention autocomplete
        const response = await fetch(
          `https://mkey-api-production.fidelismadboat.workers.dev/users/friends-search?q=${encodeURIComponent(query)}`,
          {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          }
        );
        const data = await response.json();
        setUsers(data.users || []);
        setSelectedIndex(0);
      } catch (error) {
        console.error('[MentionAutocomplete] Error searching users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 200);
    return () => clearTimeout(debounce);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (users.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % users.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + users.length) % users.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (users[selectedIndex]) {
          onSelect(users[selectedIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [users, selectedIndex, onSelect]);

  if (query.length < 1 && !loading) {
    return null;
  }

  return (
    <div
      className="mention-autocomplete"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {loading && (
        <div className="mention-loading">Buscando amigos...</div>
      )}
      {!loading && users.length === 0 && query.length >= 1 && (
        <div className="mention-empty">Nenhum amigo encontrado</div>
      )}
      {!loading && users.map((user, index) => (
        <div
          key={user.id}
          className={`mention-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => onSelect(user)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="mention-user-info">
            <div className="mention-display-name">
              {user.display_name}
              {user.is_online && <span className="mention-online-dot" />}
            </div>
            <div className="mention-username">@{user.username}</div>
          </div>
          <div className="mention-user-meta">
            <span className="mention-title">{user.odyssey_title}</span>
            <span className="mention-level">Lv.{user.odyssey_level}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
